import { useEffect, useRef, useState } from 'react';
import { testimonials } from '../data/testimonials.js';
import SectionHeading from './shared/SectionHeading.jsx';

const AUTO_MS = 5500;

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const count = testimonials.length;

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % count);
    }, AUTO_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, count]);

  const goTo = (i) => {
    setActive((i + count) % count);
    setPaused(true);
    // resume after a pause
    clearTimeout(timerRef.current);
    setTimeout(() => setPaused(false), AUTO_MS);
  };

  return (
    <section id="testimonials" className="section-pad relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute right-0 top-1/4 h-80 w-80 rounded-full bg-cyan/10 blur-[130px]" />

      <div className="container-x">
        <SectionHeading
          eyebrow="Client voices"
          title="Results our partners talk about."
          subtitle="We measure success the same way our clients do — in uptime, revenue, and engineering velocity that compounds."
        />

        <div
          className="relative mt-16"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="relative min-h-[22rem] sm:min-h-[20rem]">
            {testimonials.map((t, i) => {
              const isActive = i === active;
              return (
                <figure
                  key={t.id}
                  className="absolute inset-0 transition-all duration-700 ease-smooth-out"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.96)',
                    pointerEvents: isActive ? 'auto' : 'none',
                    zIndex: isActive ? 2 : 1,
                  }}
                  aria-hidden={!isActive}
                >
                  <div className="glass-strong relative mx-auto max-w-3xl rounded-3xl p-8 text-center shadow-card sm:p-12">
                    {/* Quote mark */}
                    <span className="absolute -top-4 left-8 font-heading text-7xl leading-none text-cyan/30">
                      &ldquo;
                    </span>
                    <blockquote className="relative text-lg font-light leading-relaxed text-silver-light sm:text-xl">
                      {t.quote}
                    </blockquote>
                    <figcaption className="mt-8 flex items-center justify-center gap-4">
                      <span className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-cyan/40 to-navy-500/50 font-heading text-base font-bold text-white shadow-glow">
                        {t.initials}
                      </span>
                      <div className="text-left">
                        <div className="font-heading text-sm font-semibold text-white">{t.author}</div>
                        <div className="text-xs text-silver/70">{t.role}</div>
                      </div>
                    </figcaption>
                  </div>
                </figure>
              );
            })}
          </div>

          {/* Controls */}
          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              onClick={() => goTo(active - 1)}
              aria-label="Previous testimonial"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white transition-all hover:border-cyan/60 hover:bg-cyan/10"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div className="flex items-center gap-2">
              {testimonials.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => goTo(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className="h-2.5 rounded-full transition-all duration-300"
                  style={{
                    width: i === active ? 28 : 8,
                    background: i === active ? '#00CFFF' : 'rgba(255,255,255,0.18)',
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => goTo(active + 1)}
              aria-label="Next testimonial"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white transition-all hover:border-cyan/60 hover:bg-cyan/10"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                <path d="M8 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
